import { Eyebrow } from "@/components/deck";
import type { ReactNode } from "react";

/**
 * The head of a route, set the way a section head is set on the release: the
 * mono code/label pair over the title in the display face, a sentence of what
 * the screen is for, and the actions pinned to the trailing edge.
 *
 * `code` is the route's operational abbreviation and comes from `lib/nav.ts` —
 * the same table the rail draws and the top bar prints, so they can never
 * disagree about what to call a screen. The rail itself stopped printing the
 * code; this header and the top bar are where an operator now reads it.
 */
export function PageHeader({
  code,
  label,
  title,
  adornment,
  description,
  actions,
}: {
  code: string;
  label: string;
  title: ReactNode;
  /** Rendered beside the title but OUTSIDE the <h1> — a status tag. */
  adornment?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <Eyebrow code={code} label={label} className="mb-4" />
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h1 className="t-display-tight min-w-0 truncate text-[clamp(1.55rem,3vw,2.05rem)] text-ink">
            {title}
          </h1>
          {adornment}
        </div>
        {description ? (
          <p className="mt-3 max-w-2xl text-[0.9375rem] leading-relaxed text-ink-2 text-pretty">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}
